import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import { selectors as paymentSelectors, selectors } from '@elab/core/store/payment';
import { priceFormatterForceNegative } from '@elab/core/utils/PriceUtils';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import connect from '@elab/core/store/connect';
import { paymentSlots } from '@elab/core/utils/constants';

/**
 * Component is used to handle payment button, this can be utilized to change the level of button,
 *  where button will change label depending on payment method selected.
 * HTML Structure
 * <e2-payment-action-button price="234">
 * </e2-payment-action-button>
 */
export class PaymentActionButton extends SimpleLitElement {
    static get properties() {
        return {
            payNowActive: { type: Boolean, attribute: false },
            actionType: { type: String, attribute: 'action-type' },
            successRedirectUrl: {
                type: String,
                attribute: 'success-redirect-url',
            },
            failureRedirectUrl: {
                type: String,
                attribute: 'failure-redirect-url',
            },
            price: { type: String, attribute: 'price' },
            selectedMethod: { type: Object, attribute: 'select-method' },
            paymentMethod: { type: String, attribute: 'payment-method' },
            zeroCreditLabel: {
                type: String,
                attribute: 'zero-credit-label',
            },
            defaultLabel: { type: String, attribute: 'default-label' },
            disabledIf: { type: String, attribute: 'disabled-if' },
            disabledRef: { type: Array, attribute: 'disabled-ref' },
            disabledConfig: { type: Array, attribute: 'disabled-config' },
            forceSyncSelection: { type: Boolean, attribute: 'force-sync-selection' },
            paymentMethodSet: { type: Boolean, attribute: false },
            klarnaLabel: { type: String, attribute: 'klarna-label' },
            enableMinimumThresholdLimit: {
                type: Boolean,
                attribute: 'enable-minimum-threshold-limit',
            },
        };
    }

    constructor() {
        super();
        this.amount = 0;
        this.price = '';
        this.paymentMethod = '';
        this.zeroCreditLabel = '';
        this.defaultLabel = '';
        this.payNowActive = true;
        this.disabledConfig = [];
        this.klarnaLabel = '';
        this.enableMinimumThresholdLimit = false;
    }

    getButtonLabel() {
        const formattedPrice = priceFormatterForceNegative(
            this.price.value,
            this.price.currencyIso,
            false,
        );
        if (
            Number(this.price.value) === 0 ||
            !this.selectedMethod.code ||
            (this.forceSyncSelection && !this.paymentMethodSet)
        ) {
            // Return label value for default, when payment is not selected
            return this.zeroCreditLabel;
        }
        if (this.selectedMethod.code.toLowerCase().indexOf(paymentSlots.CREDIT) !== -1) {
            // Return label value for button when credit card is selected
            return `${this.defaultLabel.replace('{0}', formattedPrice)}`;
        }
        if (
            this.selectedMethod.code.toLowerCase().indexOf(paymentSlots.KLARNA) !== -1 &&
            !!this.klarnaLabel &&
            this.klarnaLabel !== 'checkout.klarnapaynow.label'
        ) {
            // Return label value for button when Klarna is selected and label is set through properties
            return `${this.klarnaLabel.replace('{0}', formattedPrice)}`;
        }
        // Return label value when other payment option is selected.
        return `${this.paymentMethod
            .replace('{0}', formattedPrice)
            .replace('{1}', this.selectedMethod.name)}`;
    }

    render() {
        return html`
            <e2-action-button
                no-arrow
                full-width
                action-type="${this.actionType}"
                success-redirect-url="${this.successRedirectUrl}"
                failure-redirect-url="${this.failureRedirectUrl}"
                size="big"
                class="cart-actions__btn cart-actions__btn--checkout"
                class-modifier="button--primary"
                disabled-ref="${this.disabledRef}"
                disabled-if="payment.payNowActive"
                disabled-config="${JSON.stringify(this.disabledConfig)}"
            >
                ${this.getButtonLabel()}
            </e2-action-button>
        `;
    }
}

export const mapStateToProps = (state, ctx) => {
    const cart = cartSelectors.getData(state);
    const totalPrice = cart.amountToPay || 0;
    const getDisabledConfig = () => {
        const deliveryCost = cart.deliveryCost;
        const isCheckoutBlocked = cart.checkoutBlocked;
        if (
            ctx.enableMinimumThresholdLimit &&
            deliveryCost &&
            deliveryCost.value > 0 &&
            isCheckoutBlocked
        ) {
            return ctx.disabledConfig.filter(
                config => !Object.keys(config).includes('cart.data.checkoutBlocked'),
            );
        }

        return ctx.disabledConfig;
    };
    return {
        selectedMethod: selectors.getSelectedMethod(state),
        price: totalPrice,
        payNowActive: paymentSelectors.getPayNowActive(state),
        paymentMethodSet: selectors.getPaymentMethodSet(state),
        disabledConfig: getDisabledConfig(),
    };
};

export default () =>
    customElements.define(
        'e2-payment-action-button',
        connect(mapStateToProps, null)(PaymentActionButton),
    );
